import { useEffect, useRef, useState } from 'preact/hooks';
import { CloseButton } from './primitives.tsx';

interface ShareDraftDialogProps {
  readonly open: boolean;
  readonly title: string;
  readonly cloudAvailable: boolean;
  readonly busy: boolean;
  readonly hasUnsavedChanges: boolean;
  readonly shareUrl: string | null;
  readonly shareRevisionId: string | null;
  readonly shareCreatedAt: string | null;
  readonly stale: boolean;
  readonly message: string | null;
  readonly onClose: () => void;
  readonly onCreate: () => Promise<void>;
  readonly onRevoke: () => Promise<void>;
}

function formattedDate(value: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.valueOf())
    ? value
    : new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(date);
}

export function ShareDraftDialog(props: ShareDraftDialogProps) {
  const dialogRef = useRef<HTMLElement | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!props.open) return;
    setCopyMessage(null);
    const previousFocus = document.activeElement as HTMLElement | null;
    dialogRef.current
      ?.querySelector<HTMLElement>('button:not([disabled]), input:not([disabled])')
      ?.focus();
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        props.onClose();
        return;
      }
      if (event.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
        ),
      );
      if (focusable.length === 0) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      previousFocus?.focus();
    };
  }, [props.open]);

  useEffect(() => {
    if (!props.shareUrl) setCopyMessage(null);
  }, [props.shareUrl]);

  if (!props.open) return null;

  const copyLink = async () => {
    if (!props.shareUrl) return;
    try {
      await navigator.clipboard.writeText(props.shareUrl);
      setCopyMessage('Share Draft link copied.');
    } catch {
      setCopyMessage('Select and copy the link below.');
    }
  };
  const createdAt = formattedDate(props.shareCreatedAt);

  return (
    <div class="modal-backdrop share-draft-backdrop" role="presentation">
      <section
        ref={dialogRef}
        class="share-draft-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-draft-title"
        data-testid="share-draft-dialog"
      >
        <header>
          <div>
            <h2 id="share-draft-title">Share Draft</h2>
            <span>{props.title}</span>
          </div>
          <CloseButton label="Close Share Draft" onClick={props.onClose} />
        </header>

        <div class="share-draft-body">
          <div class="share-draft-explainer">
            <strong>Private, unpublished snapshot</strong>
            <p>
              Anyone with the full secret link can view and ride this saved version. It will not be
              listed on Buncha Games.
            </p>
          </div>

          {!props.cloudAvailable && (
            <p class="share-draft-notice" role="status">
              Sign in through Buncha Games to create a private draft link.
            </p>
          )}

          {props.shareUrl ? (
            <div class="share-draft-result">
              {props.stale && (
                <div class="share-draft-stale" role="alert">
                  <strong>This link points to an older snapshot.</strong>
                  <span>
                    Revoke it before creating a link for the latest saved draft. Existing links are
                    never silently redirected.
                  </span>
                </div>
              )}
              <label>
                <span>Secret read-only link</span>
                <input value={props.shareUrl} readOnly />
              </label>
              <dl>
                {props.shareRevisionId && (
                  <div>
                    <dt>Shared revision</dt>
                    <dd>{props.shareRevisionId}</dd>
                  </div>
                )}
                {createdAt && (
                  <div>
                    <dt>Created</dt>
                    <dd>{createdAt}</dd>
                  </div>
                )}
              </dl>
              <div class="share-draft-actions">
                <button type="button" class="primary-action" onClick={() => void copyLink()}>
                  Copy Link
                </button>
                <button
                  type="button"
                  class="danger-action"
                  disabled={props.busy}
                  onClick={() => void props.onRevoke()}
                >
                  Revoke Link
                </button>
              </div>
            </div>
          ) : (
            <button
              type="button"
              class="primary-action"
              disabled={!props.cloudAvailable || props.busy}
              onClick={() => void props.onCreate()}
            >
              {props.busy
                ? 'Preparing link…'
                : props.hasUnsavedChanges
                  ? 'Save and Create Share Draft Link'
                  : 'Create Share Draft Link'}
            </button>
          )}

          {(props.message || copyMessage) && (
            <p class="share-draft-notice" role="status">
              {copyMessage ?? props.message}
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
